import { DEFAULT_USER_SETTINGS, type UserSettings, type UserSettingsPatch } from "@chessla/shared";
import { create } from "zustand";
import { clearLegacyUserSettings, readLegacyUserSettings } from "./legacy-user-settings";
import { fetchUserSettings, migrateLegacyUserSettings, patchUserSettings } from "./user-settings.api";

interface UserSettingsState {
  ownerUserId: string | null;
  settings: UserSettings;
  updatedAt: string | null;
  loading: boolean;
  loadError: boolean;
  saveError: boolean;
  load: (userId: string | null) => Promise<void>;
  update: (settings: UserSettingsPatch) => Promise<void>;
}

let ownerEpoch = 0;
let loadEpoch = 0;
let loadController: AbortController | null = null;
let saveController: AbortController | null = null;
let saveTail: Promise<void> = Promise.resolve();
let authoritativeSettings = DEFAULT_USER_SETTINGS;
let pendingWrites: { id: number; patch: UserSettingsPatch }[] = [];
let nextWriteId = 0;

const withDefaults = (settings?: Partial<UserSettings>): UserSettings => ({ ...DEFAULT_USER_SETTINGS, ...settings });
const visibleSettings = (): UserSettings =>
  pendingWrites.reduce((settings, write) => ({ ...settings, ...write.patch }), authoritativeSettings);

export const useUserSettingsStore = create<UserSettingsState>((set, get) => ({
  ownerUserId: null,
  settings: DEFAULT_USER_SETTINGS,
  updatedAt: null,
  loading: false,
  loadError: false,
  saveError: false,
  load: async (userId) => {
    if (get().ownerUserId !== userId) {
      ownerEpoch++;
      saveController?.abort();
      saveController = null;
      authoritativeSettings = DEFAULT_USER_SETTINGS;
      pendingWrites = [];
    }
    const epoch = ownerEpoch;
    const requestLoadEpoch = ++loadEpoch;
    loadController?.abort();
    loadController = null;

    if (!userId) {
      set({
        ownerUserId: null,
        settings: DEFAULT_USER_SETTINGS,
        updatedAt: null,
        loading: false,
        loadError: false,
        saveError: false,
      });
      return;
    }

    const controller = new AbortController();
    loadController = controller;
    set({
      ownerUserId: userId,
      settings: DEFAULT_USER_SETTINGS,
      updatedAt: null,
      loading: true,
      loadError: false,
      saveError: false,
    });

    try {
      const snapshot = await fetchUserSettings(controller.signal);
      if (ownerEpoch === epoch && loadEpoch === requestLoadEpoch && get().ownerUserId === userId) {
        authoritativeSettings = withDefaults(snapshot.settings);
        set({ settings: visibleSettings(), updatedAt: snapshot.updatedAt ?? null, loading: false, loadError: false });
        const storage = typeof localStorage === "undefined" ? null : localStorage;
        const legacy = readLegacyUserSettings(storage);
        if ((snapshot.updatedAt == null || legacy.freeBoardSettings !== undefined) && Object.keys(legacy).length > 0) {
          try {
            const migrated = await migrateLegacyUserSettings(
              snapshot.updatedAt == null ? legacy : { freeBoardSettings: legacy.freeBoardSettings },
              controller.signal,
            );
            if (ownerEpoch === epoch && loadEpoch === requestLoadEpoch && get().ownerUserId === userId) {
              authoritativeSettings = withDefaults(migrated.settings);
              set({ settings: visibleSettings(), updatedAt: migrated.updatedAt, saveError: false });
              clearLegacyUserSettings(storage);
            }
          } catch {
            if (ownerEpoch === epoch && loadEpoch === requestLoadEpoch && get().ownerUserId === userId)
              set({ saveError: true });
            return;
          }
        }
      }
    } catch (error) {
      if (
        controller.signal.aborted ||
        ownerEpoch !== epoch ||
        loadEpoch !== requestLoadEpoch ||
        get().ownerUserId !== userId
      )
        return;
      set({ loading: false, loadError: true });
      throw error;
    } finally {
      if (loadController === controller) loadController = null;
    }
  },
  update: (patch) => {
    const userId = get().ownerUserId;
    if (!userId || Object.keys(patch).length === 0) return Promise.resolve();
    if (get().loading || get().loadError)
      return Promise.reject(new Error("Load your saved preferences before changing settings."));

    const epoch = ownerEpoch;
    const writeId = ++nextWriteId;
    pendingWrites.push({ id: writeId, patch });
    set({ settings: visibleSettings(), saveError: false });

    const write = saveTail.then(async () => {
      if (ownerEpoch !== epoch || get().ownerUserId !== userId) return;
      const controller = new AbortController();
      saveController = controller;
      try {
        const snapshot = await patchUserSettings(patch, controller.signal);
        if (ownerEpoch === epoch && get().ownerUserId === userId) {
          authoritativeSettings = withDefaults(snapshot.settings);
          pendingWrites = pendingWrites.filter((pending) => pending.id !== writeId);
          set({ settings: visibleSettings(), updatedAt: snapshot.updatedAt, saveError: false });
        }
      } catch (error) {
        if (ownerEpoch !== epoch || get().ownerUserId !== userId) return;
        pendingWrites = pendingWrites.filter((pending) => pending.id !== writeId);
        set({ settings: visibleSettings(), saveError: true });
        throw error;
      } finally {
        if (saveController === controller) saveController = null;
      }
    });
    saveTail = write.catch(() => undefined);
    return write;
  },
}));
